/**
 * TrainLoopDiagram: the loop as one inline SVG, drawn from the static
 * topology in trainLoop.ts with each node painted in the team's state.
 * Ink hairlines on white, serif titles, mono eyebrows; the only color is
 * the success tint on the stages that run (design-principles §3).
 *
 * Every node is a box. A gate's box has pointed ends. A stage that runs
 * is tinted and outlined in the success tone; one that is off is a plain
 * hairline box. The state word sits in the box too, so color is never the
 * only signal.
 *
 * Edges route by where the two nodes sit: neighbours in a row are joined
 * straight across, a node above another straight down (or up, for the
 * loop back), and the one long edge (the monitor's second input into
 * Benchmark PRs) rides a rail above the top row. Captions sit on the two
 * edges that need one.
 *
 * The SVG scales with the page; below a comfortable width it keeps its
 * size and the wrapper scrolls sideways, so the text never shrinks past
 * legibility. The stage list under it carries the same facts as text.
 */

import type { TrainLoopStageStatus, TrainLoopStatus } from '@/api/generated/model'
import { cn } from '@/lib/utils'

import {
  TRAIN_LOOP_EDGES,
  TRAIN_LOOP_NODES,
  STATUS_LABELS,
  type TrainLoopEdge,
  type TrainLoopNode,
  type TrainLoopStageKey,
} from './trainLoop'

const NODE_W = 150
const NODE_H = 64
const COLUMN_PITCH = 186
const ROW_PITCH = 140
const INSET = 16
/** Room above the top row for the rail the long edge rides. */
const TOP = 40
const RAIL_Y = 14
const GATE_POINT = 12
const TEXT_X = 16
const WIDTH = INSET + 4 * COLUMN_PITCH + NODE_W + INSET
const HEIGHT = TOP + ROW_PITCH + NODE_H + INSET
const ARROW_ID = 'train-loop-arrow'

interface Box {
  x: number
  y: number
}

function boxOf(node: TrainLoopNode): Box {
  return { x: INSET + node.column * COLUMN_PITCH, y: TOP + node.row * ROW_PITCH }
}

const NODES_BY_KEY: Record<TrainLoopStageKey, TrainLoopNode> = Object.fromEntries(
  TRAIN_LOOP_NODES.map((node) => [node.key, node]),
) as Record<TrainLoopStageKey, TrainLoopNode>

interface Routed {
  path: string
  /** Where the caption goes, and which way it anchors. */
  caption: { x: number; y: number; anchor: 'middle' | 'start' } | null
}

/** The polyline from one node to the next, by how the two boxes sit. */
function route(edge: TrainLoopEdge): Routed {
  const from = NODES_BY_KEY[edge.from]
  const to = NODES_BY_KEY[edge.to]
  const a = boxOf(from)
  const b = boxOf(to)
  if (from.row === to.row && Math.abs(from.column - to.column) === 1) {
    const y = a.y + NODE_H / 2
    const [x1, x2] = from.column < to.column ? [a.x + NODE_W, b.x] : [a.x, b.x + NODE_W]
    return { path: polyline([x1, y], [x2, y]), caption: null }
  }
  if (from.column === to.column) {
    const x = a.x + NODE_W / 2
    const [y1, y2] = from.row < to.row ? [a.y + NODE_H, b.y] : [a.y, b.y + NODE_H]
    return {
      path: polyline([x, y1], [x, y2]),
      caption: { x: x + 8, y: (y1 + y2) / 2 + 3, anchor: 'start' },
    }
  }
  // The long edge: up from the source, along the rail, down into the target.
  const x1 = a.x + NODE_W / 2
  const x2 = b.x + NODE_W / 2
  return {
    path: polyline([x1, a.y], [x1, RAIL_Y], [x2, RAIL_Y], [x2, b.y]),
    caption: { x: (x1 + x2) / 2, y: RAIL_Y - 5, anchor: 'middle' },
  }
}

type Point = [number, number]

/** An SVG path through the points, straight segments only. */
function polyline(...points: Point[]): string {
  return points
    .map((point, index) => [index === 0 ? 'M' : 'L', ...point].join(' '))
    .join(' ')
}

const SHAPE_CLASS: Record<TrainLoopStageStatus, string> = {
  on: 'fill-success/10 stroke-success/50',
  off: 'fill-card stroke-border',
}

const TITLE_CLASS: Record<TrainLoopStageStatus, string> = {
  on: 'fill-foreground',
  off: 'fill-muted-foreground',
}

const STATE_CLASS: Record<TrainLoopStageStatus, string> = {
  on: 'fill-success',
  off: 'fill-muted-foreground',
}

const EYEBROW_SVG = 'font-mono uppercase tracking-wider'

function gatePoints({ x, y }: Box): string {
  const mid = y + NODE_H / 2
  const corners: Point[] = [
    [x, mid],
    [x + GATE_POINT, y],
    [x + NODE_W - GATE_POINT, y],
    [x + NODE_W, mid],
    [x + NODE_W - GATE_POINT, y + NODE_H],
    [x + GATE_POINT, y + NODE_H],
  ]
  return corners.map((corner) => corner.join(',')).join(' ')
}

function Node({
  node,
  index,
  status,
}: {
  node: TrainLoopNode
  index: number
  status: TrainLoopStageStatus
}): React.JSX.Element {
  const box = boxOf(node)
  const shape = cn(SHAPE_CLASS[status], 'stroke-1')
  return (
    <g>
      {node.gate ? (
        <polygon points={gatePoints(box)} className={shape} />
      ) : (
        <rect
          x={box.x}
          y={box.y}
          width={NODE_W}
          height={NODE_H}
          rx={2}
          className={shape}
        />
      )}
      <title>{node.summary}</title>
      <text
        x={box.x + TEXT_X}
        y={box.y + 18}
        fontSize={10}
        className={cn(EYEBROW_SVG, 'fill-muted-foreground')}
      >
        {String(index).padStart(2, '0')}
        {node.gate ? ' · Human gate' : ''}
      </text>
      <text
        x={box.x + TEXT_X}
        y={box.y + 38}
        fontSize={14}
        className={cn('font-serif', TITLE_CLASS[status])}
      >
        {node.label}
      </text>
      <text
        x={box.x + TEXT_X}
        y={box.y + 54}
        fontSize={10}
        className={cn(EYEBROW_SVG, STATE_CLASS[status])}
      >
        {STATUS_LABELS[status]}
      </text>
    </g>
  )
}

function Edge({ edge }: { edge: TrainLoopEdge }): React.JSX.Element {
  const routed = route(edge)
  return (
    <g>
      <path
        d={routed.path}
        className="fill-none stroke-muted-foreground stroke-1"
        markerEnd={`url(#${ARROW_ID})`}
      />
      {edge.caption !== undefined && routed.caption !== null && (
        <text
          x={routed.caption.x}
          y={routed.caption.y}
          fontSize={9}
          textAnchor={routed.caption.anchor}
          className={cn(EYEBROW_SVG, 'fill-muted-foreground')}
        >
          {edge.caption}
        </text>
      )}
    </g>
  )
}

export function TrainLoopDiagram({ status }: { status: TrainLoopStatus }): React.JSX.Element {
  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={[0, 0, WIDTH, HEIGHT].join(' ')}
        role="img"
        aria-label="Diagram of the pipeline. Each stage shows whether it runs for this team."
        className="block w-full min-w-[56rem]"
      >
        <defs>
          <marker
            id={ARROW_ID}
            viewBox="0 0 8 8"
            refX="7"
            refY="4"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 8 4 L 0 8 z" className="fill-muted-foreground" />
          </marker>
        </defs>
        {TRAIN_LOOP_EDGES.map((edge) => (
          <Edge key={`${edge.from}-${edge.to}`} edge={edge} />
        ))}
        {TRAIN_LOOP_NODES.map((node, index) => (
          <Node key={node.key} node={node} index={index + 1} status={status[node.key].status} />
        ))}
      </svg>
    </div>
  )
}
